/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
// Free Shipping Calculator Helper Functions
// This file contains all the utility functions for the free shipping calculator

/**
 * Update subtotal display - preserves exact currency formatting from Liquid, only updates number
 * Extracts formatting pattern from initial value and applies it to new amounts
 */
function updateSubtotalDisplay(calculator, totalPrice, shopMoneyFormat, currencySymbol) {
  const subtotalAmount = calculator.querySelector('.subtotal-amount');
  if (!subtotalAmount) return;
  
  // Store the initial formatting pattern on first call (from Liquid)
  if (!subtotalAmount.dataset.formatPattern) {
    const initialValue = subtotalAmount.textContent.trim();
    
    // Extract decimal separator (comma or period)
    const hasComma = initialValue.includes(',');
    const hasPeriod = initialValue.includes('.');
    let decimalSep = '.';
    let thousandsSep = '';
    
    // Determine separators based on pattern
    // Examples: "€2.966,68" (period thousands, comma decimal) or "355,600.00 RSD" (comma thousands, period decimal)
    if (hasComma && hasPeriod) {
      // Find which comes last (that's the decimal separator)
      const lastComma = initialValue.lastIndexOf(',');
      const lastPeriod = initialValue.lastIndexOf('.');
      if (lastComma > lastPeriod) {
        decimalSep = ',';
        thousandsSep = '.';
      } else {
        decimalSep = '.';
        thousandsSep = ',';
      }
    } else if (hasComma) {
      // Could be thousands or decimal - check position
      const commaPos = initialValue.indexOf(',');
      const digitsAfter = initialValue.substring(commaPos + 1).replace(/\D/g, '').length;
      if (digitsAfter <= 2) {
        decimalSep = ',';
      } else {
        thousandsSep = ',';
        decimalSep = '.';
      }
    } else if (hasPeriod) {
      const periodPos = initialValue.indexOf('.');
      const digitsAfter = initialValue.substring(periodPos + 1).replace(/\D/g, '').length;
      if (digitsAfter <= 2) {
        decimalSep = '.';
      } else {
        thousandsSep = '.';
        decimalSep = ',';
      }
    }
    
    // Find currency symbol and position
    // Match multi-character symbols first, then single characters (order matters!)
    let symbolBefore = false;
    let actualCurrencySymbol = currencySymbol;
    // Try multi-character symbols first (RSD, BAM, BGN, EUR, USD, CHF, NOK, SEK, KM, kr, лв, РСД)
    let currencyMatch = initialValue.match(/(RSD|BAM|BGN|EUR|USD|CHF|NOK|SEK|KM|kr|лв|РСД)/i);
    if (!currencyMatch) {
      // Fall back to single character symbols
      currencyMatch = initialValue.match(/[€$£¥]/);
    }
    if (currencyMatch) {
      actualCurrencySymbol = currencyMatch[0];
      const symbolIndex = initialValue.indexOf(currencyMatch[0]);
      const firstDigitIndex = initialValue.search(/[0-9]/);
      symbolBefore = symbolIndex < firstDigitIndex;
    }
    
    // Check for space between number and currency
    const hasSpace = /\d\s+[€$£¥РСДKMлвRSDBAMBGNEURUSDCHFNOKSEK]|[€$£¥РСДKMлвRSDBAMBGNEURUSDCHFNOKSEK]\s+\d/i.test(initialValue);
    
    // Store pattern
    subtotalAmount.dataset.formatPattern = JSON.stringify({
      decimalSep: decimalSep,
      thousandsSep: thousandsSep,
      symbolBefore: symbolBefore,
      hasSpace: hasSpace,
      currencySymbol: actualCurrencySymbol
    });
  }
  
  // Parse the stored pattern
  const pattern = JSON.parse(subtotalAmount.dataset.formatPattern);
  
  // Format the number
  const priceValue = totalPrice / 100;
  const priceStr = priceValue.toFixed(2);
  const [integerPart, decimalPart] = priceStr.split('.');
  
  // Apply thousands separator
  let formattedInteger = integerPart;
  if (pattern.thousandsSep) {
    formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, pattern.thousandsSep);
  }
  
  // Combine with decimal separator
  let formattedNumber = formattedInteger + pattern.decimalSep + decimalPart;
  
  // Replace Cyrillic РСД with Latin RSD if needed
  let finalCurrencySymbol = pattern.currencySymbol;
  if (finalCurrencySymbol === 'РСД') {
    finalCurrencySymbol = 'RSD';
  }
  
  // Add currency symbol
  let formattedTotal;
  if (pattern.symbolBefore) {
    formattedTotal = finalCurrencySymbol + (pattern.hasSpace ? ' ' : '') + formattedNumber;
  } else {
    formattedTotal = formattedNumber + (pattern.hasSpace ? ' ' : '') + finalCurrencySymbol;
  }
  
  subtotalAmount.textContent = formattedTotal;
}

/**
 * Show loading spinner (theme pattern)
 */
// eslint-disable-next-line no-unused-vars
function showLoading(calculator) {
  const loadingOverlay = calculator.querySelector('.loading-overlay');
  if (loadingOverlay) {
    loadingOverlay.classList.remove('hidden');
  }
}

/**
 * Hide loading spinner (theme pattern)
 */
// eslint-disable-next-line no-unused-vars
function hideLoading(calculator) {
  const loadingOverlay = calculator.querySelector('.loading-overlay');
  if (loadingOverlay) {
    loadingOverlay.classList.add('hidden');
  }
}

/**
 * Detect customer country using available methods
 */
function detectCustomerCountry() {
  let customerCountry = 'DE'; // Default
  let detectionMethod = 'Default (DE)';
  
  if (window.Shopify && window.Shopify.country) {
    customerCountry = window.Shopify.country;
    detectionMethod = 'Shopify.country';
  } else if (window.Shopify && window.Shopify.locale && window.Shopify.locale.includes('-')) {
    customerCountry = window.Shopify.locale.split('-')[1].toUpperCase();
    detectionMethod = `Shopify.locale (${window.Shopify.locale})`;
  } else if (navigator.language && navigator.language.includes('-')) {
    customerCountry = navigator.language.split('-')[1].toUpperCase();
    detectionMethod = `Browser language (${navigator.language})`;
  } else if (window.location && window.location.pathname) {
    const pathCountry = window.location.pathname.match(/^\/([a-z]{2})\//);
    if (pathCountry) {
      customerCountry = pathCountry[1].toUpperCase();
      detectionMethod = `URL path (${window.location.pathname})`;
    }
  }
  
  return { customerCountry, detectionMethod };
}

/**
 * Find shipping settings for customer's country from loaded profiles
 */
function findShippingSettingsForCountry(shippingProfiles, countryCode, currentCartTotal, currentCartWeight) {
  if (!shippingProfiles || !Array.isArray(shippingProfiles)) {
    return null;
  }
  
  // Search through all delivery profiles to find one that covers this country
  for (const profile of shippingProfiles) {
    const profileNode = profile.node;
    
    // Check each location group/zone in this profile
    for (const locationGroup of profileNode.profileLocationGroups) {
      const zone = locationGroup.locationGroupZones.edges[0]?.node?.zone;
      if (!zone) continue;
      
      // Check if this zone covers our country
      const hasCountry = zone.countries.some(country => 
        country.code.countryCode === countryCode
      );
      
      if (hasCountry) {
        // Find the best shipping method for this cart
        const shippingMethod = findBestShippingMethod(zone, currentCartTotal / 100, currentCartWeight);
        
        return {
          zoneName: zone.name,
          profileName: profileNode.name,
          shippingMethod: shippingMethod,
          countries: zone.countries.map(c => c.code.countryCode)
        };
      }
    }
  }
  
  return null;
}

/**
 * Find the best shipping method based on cart total and weight
 */
function findBestShippingMethod(zone, cartTotal, cartWeight) {
  const methods = zone.methodDefinitions.edges;
  
  let bestMethod = null;
  let lowestCost = Infinity;
  
  for (const methodEdge of methods) {
    const method = methodEdge.node;
    
    // Check if this method's conditions are met
    const conditionsMet = method.methodConditions.every(condition => {
      return evaluateShippingCondition(condition, cartTotal, cartWeight);
    });
    
    if (conditionsMet) {
      const cost = parseFloat(method.rateProvider.price.amount);
      
      if (cost < lowestCost) {
        lowestCost = cost;
        bestMethod = method;
      }
    }
  }
  
  return bestMethod;
}

/**
 * Evaluate shipping condition (weight, price, etc.)
 */
function evaluateShippingCondition(condition, cartTotal, cartWeight) {
  const { field, operator, conditionCriteria } = condition;
  
  let actualValue;
  if (field === 'WEIGHT') {
    actualValue = cartWeight;
  } else if (field === 'TOTAL_PRICE') {
    actualValue = cartTotal;
  } else {
    return true; // Default to true for unknown fields
  }
  
  const criteriaValue = parseFloat(conditionCriteria.amount || conditionCriteria.value || 0);
  
  switch (operator) {
    case 'GREATER_THAN':
      return actualValue > criteriaValue;
    case 'GREATER_THAN_OR_EQUAL_TO':
      return actualValue >= criteriaValue;
    case 'LESS_THAN':
      return actualValue < criteriaValue;
    case 'LESS_THAN_OR_EQUAL_TO':
      return actualValue <= criteriaValue;
    case 'EQUAL_TO':
      return actualValue === criteriaValue;
    default:
      return true;
  }
}

/**
 * Check if method is free shipping (0.0 price)
 */
function isFreeShippingMethod(method) {
  return parseFloat(method.rateProvider.price.amount) === 0;
}

/**
 * Find price threshold from method conditions
 */
function findPriceThreshold(method) {
  for (const condition of method.methodConditions) {
    if (condition.field === 'TOTAL_PRICE' && condition.conditionCriteria.amount) {
      return parseFloat(condition.conditionCriteria.amount);
    }
  }
  return null;
}

/**
 * Find threshold in zone methods
 */
function findThresholdInZone(zone, targetZoneName) {
  if (zone.name !== targetZoneName) return null;
  
  for (const methodEdge of zone.methodDefinitions.edges) {
    const method = methodEdge.node;
    
    if (isFreeShippingMethod(method)) {
      const threshold = findPriceThreshold(method);
      if (threshold !== null) return threshold;
    }
  }
  
  return null;
}

/**
 * Find free shipping threshold from methods with 0.0 price
 */
function findFreeShippingThreshold(shippingSettings, shippingProfiles) {
  for (const profile of shippingProfiles) {
    const profileNode = profile.node;
    for (const locationGroup of profileNode.profileLocationGroups) {
      for (const zoneGroup of locationGroup.locationGroupZones.edges) {
        const threshold = findThresholdInZone(zoneGroup.node.zone, shippingSettings.zoneName);
        if (threshold !== null) return threshold;
      }
    }
  }
  
  return 200;
}

/**
 * Find the best shipping zone for a country (API now provides object with meaningful keys)
 */
function findBestShippingZone(shippingZones, countryCode) {
  if (!shippingZones || !countryCode) return null;

  // Convert object to array for easier processing
  const zonesArray = Object.values(shippingZones);

  // First, try exact country code match
  let bestZone = zonesArray.find(zone => 
    zone.country_codes.includes(countryCode)
  );

  // If no exact match, try country name match
  if (!bestZone) {
    bestZone = zonesArray.find(zone => 
      zone.countries.some(country => 
        country.toLowerCase().includes(countryCode.toLowerCase())
      )
    );
  }

  return bestZone;
}

/**
 * Check if weight condition is met
 */
function checkWeightCondition(condition, actualWeight) {
  const conditionWeight = condition.weight_kg;
  
  switch (condition.operator) {
    case 'GREATER_THAN': return actualWeight > conditionWeight;
    case 'GREATER_THAN_OR_EQUAL_TO': return actualWeight >= conditionWeight;
    case 'LESS_THAN': return actualWeight < conditionWeight;
    case 'LESS_THAN_OR_EQUAL_TO': return actualWeight <= conditionWeight;
    case 'EQUAL_TO': return actualWeight === conditionWeight;
    default: return true;
  }
}

/**
 * Check if price condition is met
 */
function checkPriceCondition(condition, actualAmount) {
  const conditionAmount = condition.amount;
  
  switch (condition.operator) {
    case 'GREATER_THAN': return actualAmount > conditionAmount;
    case 'GREATER_THAN_OR_EQUAL_TO': return actualAmount >= conditionAmount;
    case 'LESS_THAN': return actualAmount < conditionAmount;
    case 'LESS_THAN_OR_EQUAL_TO': return actualAmount <= conditionAmount;
    case 'EQUAL_TO': return actualAmount === conditionAmount;
    default: return true;
  }
}

/**
 * Check if rule qualifies based on all conditions
 */
function ruleQualifies(rule, cartTotal, cartWeight) {
  
  // Check weight conditions
  for (const weightCondition of rule.weight_conditions) {
    const qualifies = checkWeightCondition(weightCondition, cartWeight);
    if (!qualifies) return false;
  }
  
  // Check price conditions
  for (const priceCondition of rule.price_conditions) {
    const qualifies = checkPriceCondition(priceCondition, cartTotal);
    if (!qualifies) return false;
  }
  
  return true;
}

// Helper functions to reduce cognitive complexity
function findOriginalShippingCost(rules, cartTotal, cartWeight) {
  let originalCost = 0;
  
  for (const rule of rules) {
    if (!ruleQualifies(rule, cartTotal, cartWeight)) continue;
    if (!rule.free_shipping && rule.shipping_cost > 0) {
      if (originalCost === 0 || rule.shipping_cost < originalCost) {
        originalCost = rule.shipping_cost;
      }
    }
  }
  
  return originalCost;
}

function findBestApplicableRule(rules, cartTotal, cartWeight, thresholdMet) {
  let bestRule = null;
  let lowestCost = Infinity;
  
  for (const rule of rules) {
    if (!ruleQualifies(rule, cartTotal, cartWeight)) continue;
    
    // Skip free shipping rules if threshold not met
    if (rule.free_shipping && rule.price_conditions.length > 0 && !thresholdMet) {
      continue;
    }
    
    if (rule.shipping_cost < lowestCost) {
      bestRule = rule;
      lowestCost = rule.shipping_cost;
    }
  }
  
  return bestRule;
}

/**
 * Convert threshold from EUR to current currency (using Shopify's conversion)
 */
function convertThresholdFromEUR(thresholdInEUR, currencyCode) {
  // Get currency code from parameter or window
  const activeCurrency = currencyCode || 
    window.shopifyCurrencyCode || 
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 
    'EUR';
  
  // If already in EUR, no conversion needed
  if (activeCurrency === 'EUR') {
    return thresholdInEUR;
  }
  
  // Use Shopify's currency rate for conversion
  const currencyRate = (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
  
  if (currencyRate && currencyRate !== 1) {
    // Convert FROM EUR TO active currency by multiplying by the rate
    // (Shopify's rate converts EUR -> active currency)
    return thresholdInEUR * currencyRate;
  }
  
  // If no conversion available, return original (assume already in EUR)
  return thresholdInEUR;
}

/**
 * Calculate shipping cost for current cart (API already provides clean data)
 */
function calculateShippingCost(shippingZone, cartTotal, cartWeight, currencyCode) {
  if (!shippingZone || !shippingZone.shipping_rules) {
    return {
      cost: 0,
      free_shipping: false,
      method: null,
      threshold_met: false,
      original_cost: 0
    };
  }

  // Get threshold from API response only (no hardcoding) - threshold is in EUR cents
  const thresholdInEUR = shippingZone.free_shipping_threshold;
  
  // Convert threshold from EUR to current currency, then compare to cartTotal
  let thresholdMet = false;
  let remainingForFree = 0; // Keep in EUR cents for display (formatShippingCost expects EUR)
  if (thresholdInEUR !== null && thresholdInEUR !== undefined) {
    const thresholdInCurrentCurrency = convertThresholdFromEUR(thresholdInEUR, currencyCode);
    thresholdMet = cartTotal >= thresholdInCurrentCurrency;
    // Calculate remaining in current currency
    const remainingInCurrentCurrency = Math.max(thresholdInCurrentCurrency - cartTotal, 0);
    // Convert remaining back to EUR cents for storage (formatShippingCost will convert it back)
    const currencyRate = (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    if (currencyRate !== 1 && currencyRate > 0) {
      // Convert from current currency back to EUR: divide by rate
      remainingForFree = remainingInCurrentCurrency / currencyRate;
    } else {
      remainingForFree = remainingInCurrentCurrency;
    }
  }
  
  const originalCost = findOriginalShippingCost(shippingZone.shipping_rules, cartTotal, cartWeight);
  const bestRule = findBestApplicableRule(shippingZone.shipping_rules, cartTotal, cartWeight, thresholdMet);
  
  // Fallback if no original cost found
  let finalOriginalCost = originalCost;
  if (!finalOriginalCost && bestRule?.shipping_cost > 0) {
    finalOriginalCost = bestRule.shipping_cost;
  }
  
  const displayCost = finalOriginalCost > 0 ? finalOriginalCost : (bestRule?.shipping_cost || 0);

  return {
    cost: displayCost,
    free_shipping: bestRule ? bestRule.free_shipping : false,
    method: bestRule,
    threshold_met: thresholdMet,
    remaining_for_free: remainingForFree,
    original_cost: finalOriginalCost
  };
}


/**
 * Update progress bar width and circle position with smooth animation
 */
function updateProgressBar(calculator, progress) {
  const progressFill = calculator.querySelector('.progress-fill');
  const progressCircle = calculator.querySelector('.progress-end-circle');
  
  // Ensure progress is within bounds
  const clampedProgress = Math.min(Math.max(progress, 0), 100);
  
  if (progressFill && progressCircle) {
    // Ensure both elements start from the same position for smooth animation
    const currentFillWidth = parseFloat(progressFill.style.width) || 0;
    const currentCircleLeft = parseFloat(progressCircle.style.left) || 0;
    
    // If this is the first update (both at 0), set initial positions
    if (currentFillWidth === 0 && currentCircleLeft === 0) {
      // Set both to 0% initially (circle offset to 2.5% to stay within bounds)
      progressFill.style.width = '0%';
      progressCircle.style.left = '2.5%';
      
      // Force a reflow to ensure the initial state is applied
      // eslint-disable-next-line no-unused-expressions
      progressFill.offsetHeight;
      // eslint-disable-next-line no-unused-expressions
      progressCircle.offsetHeight;
    }
    
    // Use requestAnimationFrame to ensure smooth animation
    requestAnimationFrame(() => {
      progressFill.style.width = `${clampedProgress}%`;
      // Offset circle position to keep it within bounds (maps 0-100% to 2.5-97.5%)
      const circlePosition = 2.5 + (clampedProgress * 0.95);
      progressCircle.style.left = `${circlePosition}%`;
    });
  }
}

/**
 * Update shipping message text
 */
function updateShippingMessage(calculator, isFreeShipping, remainingAmount, translations, currencySymbol, currencyCode, moneyFormat) {
  const shippingText = calculator.querySelector('.shipping-text');
  if (shippingText) {
    if (isFreeShipping) {
      shippingText.innerHTML = translations.achieved;
    } else {
      // remainingAmount is in EUR (cents converted to euros), convert to active currency
      const formattedRemaining = formatShippingCost(calculator, remainingAmount, moneyFormat, currencySymbol, currencyCode);
      
      // Replace the spinner with the amount
      const spinner = shippingText.querySelector('.loading-spinner');
      if (spinner) {
        spinner.outerHTML = `<strong>${formattedRemaining}</strong>`;
      } else {
        // Fallback if no spinner found
        shippingText.innerHTML = `${translations.only} <strong>${formattedRemaining}</strong> ${translations.leftUntilFree}`;
      }
    }
  }
}

/**
 * Format money value using Shopify's currency conversion system
 * Converts EUR amounts to customer's active currency
 */
function formatShippingCost(calculator, costInEUR, moneyFormat, currencySymbol, currencyCode) {
  // Get Shopify's active currency and rate
  const activeCurrency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || currencyCode || 'EUR';
  const currencyRate = (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
  
  // Convert from EUR to active currency
  let convertedCost = costInEUR;
  if (activeCurrency !== 'EUR' && currencyRate && currencyRate !== 1) {
    convertedCost = costInEUR * currencyRate;
  }
  
  // Apply rounding rules only for non-EUR currencies
  let finalCost = convertedCost;
  if (activeCurrency !== 'EUR') {
    if (convertedCost >= 1000) {
      // 4 digits or more: round to nearest 100 (upper)
      finalCost = Math.ceil(convertedCost / 100) * 100;
    } else {
      // Below 4 digits: round UP to nearest 1
      finalCost = Math.ceil(convertedCost);
    }
  }
  // For EUR, keep exact decimals (no rounding)
  
  // Format using Shopify's formatMoney if available
  if (window.Shopify && window.Shopify.formatMoney) {
    // Convert to cents (multiply by 100) for Shopify.formatMoney
    const costInCents = finalCost * 100;
    const formatted = window.Shopify.formatMoney(costInCents, moneyFormat);
    if (formatted && formatted.trim().length > 0) {
      return formatted;
    }
  }
  
  // Fallback: format manually
  // Match the decimal separator format from any existing Liquid-formatted price
  let decimalSep = ','; // Default to comma for EUR (European format)
  let thousandsSep = '.';

  // Try to find a reference price element to detect the format
  const referencePrice = document.querySelector('.totals__subtotal-value') ||
                         document.querySelector('.cart-item__final-price') ||
                         calculator.querySelector('.subtotal-amount');

  if (referencePrice) {
    const priceText = referencePrice.textContent.trim();
    // Check format: if comma comes after period, comma is decimal separator (European)
    // if period comes after comma, period is decimal separator (US/UK)
    const lastComma = priceText.lastIndexOf(',');
    const lastPeriod = priceText.lastIndexOf('.');

    if (lastComma > -1 && lastPeriod > -1) {
      if (lastComma > lastPeriod) {
        decimalSep = ',';
        thousandsSep = '.';
      } else {
        decimalSep = '.';
        thousandsSep = ',';
      }
    } else if (lastComma > -1) {
      // Only comma - check if it's decimal (2 digits after) or thousands
      const afterComma = priceText.substring(lastComma + 1).replace(/\D/g, '');
      if (afterComma.length <= 2) {
        decimalSep = ',';
        thousandsSep = '.';
      }
    } else if (lastPeriod > -1) {
      // Only period - check if it's decimal (2 digits after) or thousands
      const afterPeriod = priceText.substring(lastPeriod + 1).replace(/\D/g, '');
      if (afterPeriod.length <= 2) {
        decimalSep = '.';
        thousandsSep = ',';
      }
    }
  }
  
  // Format with 2 decimal places, using the same separators as subtotal
  const priceStr = finalCost.toFixed(2);
  const [integerPart, decimalPart] = priceStr.split('.');
  
  // Apply thousands separator
  let formattedInteger = integerPart;
  if (thousandsSep) {
    formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
  }
  
  // Combine with decimal separator (matching subtotal format)
  const formattedAmount = formattedInteger + decimalSep + decimalPart;
  
  let result = moneyFormat.replace(/\{\{amount[^}]*\}\}/g, formattedAmount);
  
  // Ensure currency symbol is included
  if (!result.includes(currencySymbol) && !result.match(/[€$£¥krCHFlei]/)) {
    const amountPlaceholderMatch = moneyFormat.match(/\{\{amount[^}]*\}\}/);
    if (amountPlaceholderMatch) {
      const placeholderPos = moneyFormat.indexOf(amountPlaceholderMatch[0]);
      if (placeholderPos === 0) {
        result = formattedAmount + ' ' + currencySymbol;
      } else {
        result = currencySymbol + formattedAmount;
      }
    } else {
      result = formattedAmount + ' ' + currencySymbol;
    }
  }
  
  return result;
}

/**
 * Update final cost display - always show original shipping cost
 */
function updateFinalCostDisplay(calculator, isFreeShipping, shippingCost, translations, currencySymbol, currencyCode, originalCost, moneyFormat) {
  const finalCost = calculator.querySelector('.final-cost');
  if (finalCost) {
    // Hide the loading spinner
    const spinner = finalCost.querySelector('.loading-spinner');
    if (spinner) {
      spinner.style.display = 'none';
    }
    
    // Always show the original shipping cost, even when free shipping is achieved
    // shippingCost and originalCost are in EUR from the API
    const costToShow = originalCost || shippingCost;
    
    // Convert and format using Shopify's currency system
    const formattedCost = formatShippingCost(calculator, costToShow, moneyFormat, currencySymbol, currencyCode);
    
    finalCost.textContent = formattedCost;
    finalCost.style.fontWeight = '500';
    finalCost.style.color = 'rgba(var(--color-foreground), 0.6)';
    
    // Add line-through styling when free shipping is achieved
    if (isFreeShipping) {
      finalCost.classList.add('free-shipping-achieved');
    } else {
      finalCost.classList.remove('free-shipping-achieved');
    }
  }
}

/**
 * Update zone name in dev details
 */
function updateZoneDisplay(calculator, zoneName) {
  const zoneValue = calculator.querySelector('.zone-value');
  if (zoneValue) {
    zoneValue.textContent = zoneName;
  }
}

/**
 * Update shipping value in dev details - always show original cost
 */
function updateShippingValueDisplay(calculator, isFreeShipping, shippingCost, translations, currencySymbol, currencyCode, originalCost, moneyFormat) {
  const shippingValue = calculator.querySelector('.shipping-value');
  if (shippingValue) {
    // Always show the original shipping cost, even when free shipping is achieved
    // shippingCost and originalCost are in EUR from the API
    const costToShow = originalCost || shippingCost;
    
    // Convert and format using Shopify's currency system
    const formattedCost = formatShippingCost(calculator, costToShow, moneyFormat, currencySymbol, currencyCode);
    shippingValue.textContent = formattedCost;
  }
}

/**
 * Update calculator display with shipping information
 */
function updateCalculatorDisplay(calculator, shippingZone, currentCartTotal, currentCartWeight, shippingCalculation, shopMoneyFormat, currencySymbol, currencyCode, translations) {
  if (!shippingZone) {
    return;
  }

  const currentShippingCost = shippingCalculation.cost;
  const freeShippingThreshold = shippingZone.free_shipping_threshold; // Keep in cents for calculation
  const zoneName = shippingZone.name;

  // Handle zones without free shipping - still show shipping info but no progress
  if (!freeShippingThreshold) {
    // No free shipping available in this zone - hide progress but show shipping cost
    const shippingMessage = calculator.querySelector('.shipping-message');
    const progressContainer = calculator.querySelector('.progress-container');

    if (shippingMessage) shippingMessage.style.display = 'none'; // Hide free shipping message
    if (progressContainer) progressContainer.style.display = 'none'; // Hide progress bar

    // Still show subtotal and shipping cost (this is the important part!)
    updateSubtotalDisplay(calculator, currentCartTotal, shopMoneyFormat, currencySymbol);
    updateFinalCostDisplay(calculator, false, currentShippingCost, translations, currencySymbol, currencyCode, shippingCalculation.original_cost, shopMoneyFormat);
    updateZoneDisplay(calculator, zoneName);
    updateShippingValueDisplay(calculator, false, currentShippingCost, translations, currencySymbol, currencyCode, shippingCalculation.original_cost, shopMoneyFormat);
    return;
  }
  
  // Convert threshold from EUR to current currency for progress calculation
  const thresholdInCurrentCurrency = convertThresholdFromEUR(freeShippingThreshold, currencyCode);
  const progress = Math.min((currentCartTotal / thresholdInCurrentCurrency) * 100, 100);
  const isFreeShipping = shippingCalculation.free_shipping || progress >= 100;
  // remaining_for_free is in EUR cents, convert to EUR decimal for formatShippingCost
  const remainingAmount = shippingCalculation.remaining_for_free ? shippingCalculation.remaining_for_free / 100 : 0; // Convert from EUR cents to EUR decimal

  // Show free shipping elements since this zone has free shipping
  const shippingMessage = calculator.querySelector('.shipping-message');
  const progressContainer = calculator.querySelector('.progress-container');

  if (shippingMessage) shippingMessage.style.display = '';
  if (progressContainer) progressContainer.style.display = '';
  
  // Update all UI elements using dedicated functions
  updateSubtotalDisplay(calculator, currentCartTotal, shopMoneyFormat, currencySymbol);
  updateProgressBar(calculator, progress);
  updateShippingMessage(calculator, isFreeShipping, remainingAmount, translations, currencySymbol, currencyCode, shopMoneyFormat);
  updateFinalCostDisplay(calculator, isFreeShipping, currentShippingCost, translations, currencySymbol, currencyCode, shippingCalculation.original_cost, shopMoneyFormat);
  updateZoneDisplay(calculator, zoneName);
  updateShippingValueDisplay(calculator, isFreeShipping, currentShippingCost, translations, currencySymbol, currencyCode, shippingCalculation.original_cost, shopMoneyFormat);
}
